import { useState } from "react";
import { z } from "zod";
import { Mail, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const schema = z.object({
  email: z.string().trim().email("Email non valida").max(255),
  nome: z.string().trim().max(100).optional().or(z.literal("")),
});

export const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [consenso, setConsenso] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consenso) {
      toast.error("Devi accettare l'informativa privacy.");
      return;
    }
    const parsed = schema.safeParse({ email, nome });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: parsed.data.email,
      nome: parsed.data.nome || null,
      consenso: true,
    });
    setLoading(false);
    if (error) {
      if (error.code === "23505") {
        toast.success("Sei già iscritto. Grazie!");
        setEmail("");
        setNome("");
        setConsenso(false);
        return;
      }
      toast.error("Errore. Riprova più tardi.");
      return;
    }
    toast.success("Iscrizione completata! Riceverai la prossima newsletter.");
    setEmail("");
    setNome("");
    setConsenso(false);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" aria-label="Iscrizione newsletter">
      <div className="flex items-center gap-2 text-secondary font-semibold">
        <Mail size={16} aria-hidden="true" />
        <span className="text-sm uppercase tracking-wider">Newsletter mensile</span>
      </div>
      <p className="text-xs text-primary-foreground/80 leading-relaxed">
        Aggiornamenti normativi, casi studio e inviti agli eventi FIABA. Una mail al mese, niente spam.
      </p>
      <div className="space-y-2">
        <label htmlFor="nl-nome" className="sr-only">Nome (opzionale)</label>
        <input
          id="nl-nome"
          type="text"
          placeholder="Nome (opzionale)"
          maxLength={100}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className="w-full rounded-md bg-primary-foreground/10 border border-primary-foreground/20 px-3 py-2 text-sm text-primary-foreground placeholder:text-primary-foreground/50 focus:outline-none focus:ring-2 focus:ring-secondary"
        />
        <label htmlFor="nl-email" className="sr-only">Email</label>
        <div className="flex gap-2">
          <input
            id="nl-email"
            type="email"
            required
            placeholder="La tua email"
            maxLength={255}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-md bg-primary-foreground/10 border border-primary-foreground/20 px-3 py-2 text-sm text-primary-foreground placeholder:text-primary-foreground/50 focus:outline-none focus:ring-2 focus:ring-secondary"
          />
          <button
            type="submit"
            disabled={loading}
            aria-label="Iscriviti alla newsletter"
            className="inline-flex items-center justify-center rounded-md bg-gold text-secondary-foreground px-3 py-2 font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Send size={16} aria-hidden="true" />
            <span className="sr-only">Iscriviti</span>
          </button>
        </div>
      </div>
      <label className="flex items-start gap-2 text-xs text-primary-foreground/80 cursor-pointer">
        <input
          type="checkbox"
          checked={consenso}
          onChange={(e) => setConsenso(e.target.checked)}
          className="mt-0.5 accent-secondary"
        />
        <span>
          Acconsento al trattamento dei dati per l'invio della newsletter (
          <a href="https://www.iubenda.com/privacy-policy/85586417" target="_blank" rel="noopener noreferrer" className="underline hover:text-secondary">
            Privacy policy
          </a>
          ).
        </span>
      </label>
    </form>
  );
};
